import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { yupResolver } from '@hookform/resolvers/yup';
import http from 'apis/config/http-service';
import { useCreateErpIntegrationApi } from 'apis/erp-mappings/use-create-erp-integration-api';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

type TOption = {
  value: string | number;
  label: string;
};

interface ICreateIntegrationFormInputs {
  restaurant: TOption | null;
  supplierType: TOption | null;
  supplier: TOption | null;
}

const createIntegrationSchema = yup.object().shape({
  restaurant: yup.object().nullable().required('Restaurant is required'),
  supplierType: yup.object().nullable().required('Supplier type is required'),
  supplier: yup.object().nullable().required('Supplier is required'),
});

const defaultCreateValues: ICreateIntegrationFormInputs = {
  restaurant: null,
  supplierType: null,
  supplier: null,
};

const supplierTypeOptions: TOption[] = [
  { value: 'restaurant_warehouse', label: 'Warehouse' },
  { value: 'supplier', label: 'Supplier' },
];

interface ICreateIntegrationModal {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateIntegrationModal: React.FC<ICreateIntegrationModal> = ({ isOpen, onClose }) => {
  const toast = useCustomToast();
  const prevSupplierTypeRef = useRef<TOption | null>(null);
  const currentRestaurantInputRef = useRef<string>('');
  const currentSupplierInputRef = useRef<string>('');
  const currentWarehouseInputRef = useRef<string>('');

  // Pagination state
  const [restaurantOptions, setRestaurantOptions] = useState<TOption[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<TOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<TOption[]>([]);
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [warehousePage, setWarehousePage] = useState(1);
  const [restaurantHasMore, setRestaurantHasMore] = useState(true);
  const [supplierHasMore, setSupplierHasMore] = useState(true);
  const [warehouseHasMore, setWarehouseHasMore] = useState(true);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<ICreateIntegrationFormInputs>({
    resolver: yupResolver(createIntegrationSchema),
    mode: 'onChange',
    defaultValues: defaultCreateValues,
  });

  const watchedRestaurant = watch('restaurant');
  const watchedSupplierType = watch('supplierType');

  // Clear supplier dropdown when supplier type changes
  useEffect(() => {
    // Only clear if there was a previous supplier type and it's different from current
    if (prevSupplierTypeRef.current && prevSupplierTypeRef.current.value !== watchedSupplierType?.value) {
      setValue('supplier', null);
      // Reset supplier/warehouse pagination state
      setSupplierOptions([]);
      setWarehouseOptions([]);
      setSupplierPage(1);
      setWarehousePage(1);
      setSupplierHasMore(true);
      setWarehouseHasMore(true);
    }
    // Update the ref with current value
    prevSupplierTypeRef.current = watchedSupplierType;
  }, [watchedSupplierType, setValue]);

  const createIntegrationMutation = useCreateErpIntegrationApi({
    onSuccess: () => {
      toast({
        description: 'ERP integration created successfully',
        status: 'success',
      });
      onClose();
      reset(defaultCreateValues);
    },
  });

  // Load restaurants with search and pagination
  const loadRestaurantOptions = (inputValue: string, callback: any, { page }: { page?: number } = {}) => {
    const currentPage = page || 1;
    const isNewSearch = !page; // First load or new search

    // Track current input value
    currentRestaurantInputRef.current = inputValue;

    http
      .get('restaurants', {
        params: {
          page: currentPage,
          limit: 20,
          filter: inputValue ? `nameEn||$contL||${inputValue}` : undefined,
        },
      })
      .then((res) => {
        const restaurants = res?.data?.data || [];
        const totalPages = res?.data?.last_page || 1;
        const newOptions: TOption[] = [];

        restaurants.forEach((item: any) => {
          newOptions.push({ value: item?.id, label: item?.name });
        });

        if (isNewSearch) {
          // First load or new search - replace options
          setRestaurantOptions(newOptions);
          setRestaurantPage(1);
          setRestaurantHasMore(currentPage < totalPages);
          callback(newOptions);
        } else {
          // Load more - append options
          const updatedOptions = [...restaurantOptions, ...newOptions];
          setRestaurantOptions(updatedOptions);
          setRestaurantPage(currentPage);
          setRestaurantHasMore(currentPage < totalPages);
          callback(updatedOptions);
        }
      })
      .catch(() => {
        callback(isNewSearch ? [] : restaurantOptions);
      });
  };

  // Load suppliers with search and pagination
  const loadSupplierOptions = (inputValue: string, callback: any, { page }: { page?: number } = {}) => {
    const currentPage = page || 1;
    const isNewSearch = !page; // First load or new search

    // Track current input value
    currentSupplierInputRef.current = inputValue;

    http
      .get('suppliers', {
        params: {
          page: currentPage,
          limit: 20,
          filter: inputValue ? `name||$contL||${inputValue}` : undefined,
        },
      })
      .then((res) => {
        const suppliers = res?.data?.data || [];
        const totalPages = res?.data?.last_page || 1;
        const newOptions: TOption[] = [];

        suppliers.forEach((item: any) => {
          newOptions.push({ value: item?.id, label: item?.name });
        });

        if (isNewSearch) {
          // First load or new search - replace options
          setSupplierOptions(newOptions);
          setSupplierPage(1);
          setSupplierHasMore(currentPage < totalPages);
          callback(newOptions);
        } else {
          // Load more - append options
          const updatedOptions = [...supplierOptions, ...newOptions];
          setSupplierOptions(updatedOptions);
          setSupplierPage(currentPage);
          setSupplierHasMore(currentPage < totalPages);
          callback(updatedOptions);
        }
      })
      .catch(() => {
        callback(isNewSearch ? [] : supplierOptions);
      });
  };

  // Load warehouses with search and pagination
  const loadWarehouseOptions = (inputValue: string, callback: any, { page }: { page?: number } = {}) => {
    if (!watchedRestaurant?.value) {
      callback([]);
      return;
    }

    const currentPage = page || 1;
    const isNewSearch = !page; // First load or new search

    // Track current input value
    currentWarehouseInputRef.current = inputValue;

    http
      .get(`ums/v1/restaurants/warehouses/${watchedRestaurant.value}`, {
        params: {
          page: currentPage,
          limit: 20,
          filter: inputValue ? `name||$contL||${inputValue}` : undefined,
        },
      })
      .then((res) => {
        const warehouses = res?.data?.data || [];
        const totalPages = res?.data?.last_page || 1;
        const newOptions: TOption[] = [];

        warehouses.forEach((item: any) => {
          newOptions.push({
            value: `${watchedRestaurant.value}_${item?.id}`,
            label: item?.name,
          });
        });

        if (isNewSearch) {
          // First load or new search - replace options
          setWarehouseOptions(newOptions);
          setWarehousePage(1);
          setWarehouseHasMore(currentPage < totalPages);
          callback(newOptions);
        } else {
          // Load more - append options
          const updatedOptions = [...warehouseOptions, ...newOptions];
          setWarehouseOptions(updatedOptions);
          setWarehousePage(currentPage);
          setWarehouseHasMore(currentPage < totalPages);
          callback(updatedOptions);
        }
      })
      .catch(() => {
        callback(isNewSearch ? [] : warehouseOptions);
      });
  };

  const onSubmit = (data: ICreateIntegrationFormInputs) => {
    if (!data.restaurant || !data.supplier || !data.supplierType) return;

    // Extract supplier ID based on supplier type
    let supplierId: number;
    if (data.supplierType.value === 'restaurant_warehouse') {
      // For warehouse, value is in format "restaurantId_warehouseId"
      const warehouseId = data.supplier.value.toString().split('_')[1];
      supplierId = Number(warehouseId);
    } else {
      // For supplier, value is just the supplier ID
      supplierId = Number(data.supplier.value);
    }

    createIntegrationMutation.mutate({
      body: {
        restaurantId: Number(data.restaurant.value),
        supplierId: supplierId,
        supplierType: data.supplierType.value as string,
      },
    });
  };

  // Reset pagination state
  const resetPaginationState = () => {
    setRestaurantOptions([]);
    setSupplierOptions([]);
    setWarehouseOptions([]);
    setRestaurantPage(1);
    setSupplierPage(1);
    setWarehousePage(1);
    setRestaurantHasMore(true);
    setSupplierHasMore(true);
    setWarehouseHasMore(true);
  };

  // Load more functions for pagination
  const loadMoreRestaurants = (inputValue: string) => {
    if (restaurantHasMore) {
      loadRestaurantOptions(
        inputValue,
        () => {
          // Page loaded, state updated in loadRestaurantOptions
        },
        { page: restaurantPage + 1 }
      );
    }
  };

  const loadMoreSuppliers = (inputValue: string) => {
    if (supplierHasMore) {
      loadSupplierOptions(
        inputValue,
        () => {
          // Page loaded, state updated in loadSupplierOptions
        },
        { page: supplierPage + 1 }
      );
    }
  };

  const loadMoreWarehouses = (inputValue: string) => {
    if (warehouseHasMore) {
      loadWarehouseOptions(
        inputValue,
        () => {
          // Page loaded, state updated in loadWarehouseOptions
        },
        { page: warehousePage + 1 }
      );
    }
  };

  const handleClose = () => {
    reset(defaultCreateValues);
    prevSupplierTypeRef.current = null;
    currentRestaurantInputRef.current = '';
    currentSupplierInputRef.current = '';
    currentWarehouseInputRef.current = '';
    resetPaginationState();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create ERP Integration</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack spacing={6} align='stretch'>
              <Controller
                name='restaurant'
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <FormControl isInvalid={!!errors.restaurant}>
                    <FormLabel>Restaurant</FormLabel>
                    <ChakraAsyncSelect
                      onChange={onChange}
                      name={name}
                      onBlur={onBlur}
                      value={value}
                      loadOptions={loadRestaurantOptions}
                      placeholder='Type to search restaurants...'
                      isClearable
                      cacheOptions
                      defaultOptions
                      noOptionsMessage={() => 'No restaurants found'}
                      onMenuScrollToBottom={() => {
                        if (restaurantHasMore) {
                          loadMoreRestaurants(currentRestaurantInputRef.current);
                        }
                      }}
                    />
                    <FormErrorMessage>{errors.restaurant?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <Controller
                name='supplierType'
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <FormControl isInvalid={!!errors.supplierType}>
                    <FormLabel>Supplier Type</FormLabel>
                    <ChakraAsyncSelect
                      onChange={onChange}
                      name={name}
                      onBlur={onBlur}
                      value={value}
                      loadOptions={(inputValue, callback) => {
                        const filtered = supplierTypeOptions.filter((option) =>
                          option.label.toLowerCase().includes(inputValue.toLowerCase())
                        );
                        callback(filtered);
                      }}
                      placeholder='Select supplier type'
                      isClearable
                      cacheOptions
                      defaultOptions={supplierTypeOptions}
                    />
                    <FormErrorMessage>{errors.supplierType?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <Controller
                name='supplier'
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <FormControl isInvalid={!!errors.supplier}>
                    <FormLabel>
                      {watchedSupplierType?.value === 'restaurant_warehouse' ? 'Warehouse' : 'Supplier'}
                    </FormLabel>
                    <ChakraAsyncSelect
                      key={watchedSupplierType?.value || 'no-supplier-type'}
                      onChange={onChange}
                      name={name}
                      onBlur={onBlur}
                      value={value}
                      loadOptions={
                        watchedSupplierType?.value === 'restaurant_warehouse'
                          ? loadWarehouseOptions
                          : loadSupplierOptions
                      }
                      placeholder={`Type to search ${
                        watchedSupplierType?.value === 'restaurant_warehouse'
                          ? 'warehouse'
                          : watchedSupplierType?.value || 'supplier'
                      }s...`}
                      isDisabled={!watchedSupplierType}
                      isClearable
                      cacheOptions={false}
                      defaultOptions
                      noOptionsMessage={() =>
                        `No ${
                          watchedSupplierType?.value === 'restaurant_warehouse'
                            ? 'warehouse'
                            : watchedSupplierType?.value || 'supplier'
                        }s found`
                      }
                      onMenuScrollToBottom={() => {
                        if (watchedSupplierType?.value === 'restaurant_warehouse' && warehouseHasMore) {
                          loadMoreWarehouses(currentWarehouseInputRef.current);
                        } else if (watchedSupplierType?.value === 'supplier' && supplierHasMore) {
                          loadMoreSuppliers(currentSupplierInputRef.current);
                        }
                      }}
                    />
                    <FormErrorMessage>{errors.supplier?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant='ghost' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type='submit'
                colorScheme='blue'
                isLoading={createIntegrationMutation.isLoading}
                loadingText='Creating...'
                isDisabled={!isValid}>
                Create Integration
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
